<template>
  <div class="mb-10 mt-6 b-contract__assets">
    <div class="b-contract__assets-top-panel mx-6 mb-4">
      <div class="b-contract__assets-top-panel-item">
        <h2>{{ $t('CONTENT.assets.rent_objects') }} ({{ groupFilteredAssetComponentsByAssetId.length }})</h2>
        <div class="b-contract__assets-subinfo mt-4"
             v-if="contractObj.contract.turnoverPercentage || contractObj.contract.revenues">
          <div class="b-contract__assets-subinfo-item mr-6">
            <div class="b-contract__assets-subinfo-name mr-2">{{ $t('CONTENT.contract.turnover') }}:</div>
            <div class="b-contract__assets-subinfo-value">
              <span v-if="contractObj.contract.revenues">{{ contractObj.contract.revenues }} {{ currencyCode }}</span>
              <span v-if="contractObj.contract.revenues && contractObj.contract.turnoverPercentage">, </span>
              <span v-if="contractObj.contract.turnoverPercentage">{{ contractObj.contract.turnoverPercentage }}%</span>
            </div>
          </div>
        </div>
      </div>
      <div class="b-contract__assets-top-panel-item" v-if="isContractRawEditing && (!isInClosedPeriod || contractObj.contract.status === 1)">
        <button type="button" class="b-btn" @click="showEditAssetModal">
          <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
          {{ $t('CONTENT.assets.add_asset') }}
        </button>
      </div>
    </div>
    <div class="b-full-table">
      <div class="b-full-table__row b-full-table__row-header">
        <div class="b-full-table__cell b-full-table__header-cell">{{ $tc('CONTENT.assets.asset', 1) }} /
          {{ $t('CONTENT.assets.component') }}
        </div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.assets.leased_amount') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.assets.rent_period') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.assets.extension') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.assets.termination') }}</div>
        <div class="b-full-table__cell b-full-table__header-cell">{{ $t('CONTENT.assets.buyout') }}</div>
      </div>
      <template
        v-for="(assetComponentsGroupArray, assetComponentsGroupArrayIndex) in groupFilteredAssetComponentsByAssetId">
        <div v-if="assetComponentsGroupArray.length === 1" :key="assetComponentsGroupArrayIndex">
          <div class="b-full-table__row">
            <template v-for="assetComponent in assetComponentsGroupArray">
              <div class="b-full-table__cell b-full-table__cell_type_flex">
                <button class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
                        @click="showEditAssetComponentModal(assetComponent, false)"
                        v-if="isContractRawEditing && (!isInClosedPeriod || contractObj.contract.status === 1)">
                  <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.editAssetComponent')"
                                icon="edit"></dynamic-icon>
                </button>
                <button class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
                        @click="showEditAssetComponentModal(assetComponent, true)"
                        v-if="isContractRawSimplifiedEditing || (isContractRawEditing && isInClosedPeriod && contract.status !== 1)">
                  <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.editAssetComponent')"
                                icon="edit"></dynamic-icon>
                </button>
                <button class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
                        v-if="isContractChangeConditions && !isContractEventState"
                        @click="showItem(assetComponent, groupFilteredAssetComponentsByAssetId.length)">
                  <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.changeConditionsAssetComponentHint')"
                                icon="edit"></dynamic-icon>
                </button>
                <div class="b-full-table__arrow"
                     :class="{'b-full-table__arrow_type_open' : isGroupedAssetComponentsShow(assetComponentsGroupArrayIndex)}"
                     @click="setChosenGroupedAssetComponents(assetComponentsGroupArrayIndex)"></div>
                <div>
                  {{ getAssetName(assetComponent.assetId) }}
                  <div class="b-contract__secondary-text">id: {{ assetComponent.assetId }}</div>
                  <div v-if="assetComponent.externalId"
                       class="b-contract__secondary-text">{{ $t('CONTENT.assets.externalId') }}:
                    {{ assetComponent.externalId }}
                  </div>
                </div>
              </div>
              <div class="b-full-table__cell">{{ assetComponent.quantity }}
                {{ getUnitOfMeasureName(assetComponent.unitOfMeasureCode) }}
              </div>
              <div class="b-full-table__cell">{{ assetComponent.startDate | dateFormatted }} -
                {{ assetComponent.endDate | dateFormatted }}
              </div>
              <div class="b-full-table__cell b-full-table__cell_type_flex">
                <template v-if="getIsExtension(assetComponent)">
                  <dynamic-icon icon="check" class="b-icon_color_blue mr-2"/>
                  <div v-if="assetComponent.extension.date !== null">{{ getExtensionDate(assetComponent) }}</div>
                  <div v-else>{{ $t('CONTENT.assets.option_true') }}</div>
                </template>
              </div>
              <div class="b-full-table__cell b-full-table__cell_type_flex">
                <template v-if="getIsTermination(assetComponent)">
                  <dynamic-icon icon="check" class="b-icon_color_red mr-2"/>
                  <div v-if="assetComponent.termination.date !== null">{{ getTerminationDate(assetComponent) }}</div>
                  <div v-else>{{ $t('CONTENT.assets.option_true') }}</div>
                </template>
              </div>
              <div class="b-full-table__cell b-full-table__cell_type_flex">
                <template v-if="getIsBuyout(assetComponent)">
                  <dynamic-icon icon="check" class="b-icon_color_green mr-2"/>
                  <div v-if="assetComponent.buyout.date !== null">{{ getBuyoutDate(assetComponent) }}</div>
                  <div v-else>{{ $t('CONTENT.assets.option_true') }}</div>
                </template>
              </div>
            </template>
          </div>
          <div>
            <transition name="fade" v-if="isGroupedAssetComponentsShow(assetComponentsGroupArrayIndex)">
              <template v-for="assetComponent in assetComponentsGroupArray">
                <AssetComponentTableRow :assetComponent="assetComponent"
                                        :componentsLength="assetComponentsGroupArray.length"
                                        @onLessorComponentShareUpdated="onLessorComponentShareUpdated"/>
              </template>
            </transition>
          </div>
        </div>
        <div v-if="assetComponentsGroupArray.length > 1" :key="assetComponentsGroupArrayIndex">
          <div class="b-full-table__row">
            <div class="b-full-table__cell b-full-table__cell_type_flex">
              <button
                class="b-btn b-btn_type_empty b-full-table__edit px-0 b-btn_type_icon_accent_color"
                v-if="isContractChangeConditions && !isContractEventState"
                @click="setAssetItem(assetComponentsGroupArray)"
              >
                <dynamic-icon class="b-btn__icon" v-tooltip="$t('CONTENT.hints.changeConditionsAssetHint')"
                              icon="edit"></dynamic-icon>
              </button>
              <div class="b-full-table__arrow"
                   :class="{'b-full-table__arrow_type_open' : isGroupedAssetComponentsShow(assetComponentsGroupArrayIndex)}"
                   @click="setChosenGroupedAssetComponents(assetComponentsGroupArrayIndex)"></div>
              <div>
                {{ getAssetName(assetComponentsGroupArray[0].assetId) }} - {{ $t('CONTENT.assets.assetСomponents') }}:
                {{ assetComponentsGroupArray.length }}
                <div class="b-contract__secondary-text">id: {{ assetComponentsGroupArray[0].assetId }}</div>
                <div v-if="assetComponentsGroupArray[0].assetExternalId"
                     class="b-contract__secondary-text">{{ $t('CONTENT.assets.externalId') }}:
                  {{ assetComponentsGroupArray[0].assetExternalId }}
                </div>
              </div>
            </div>
          </div>
          <transition name="fade" v-if="isGroupedAssetComponentsShow(assetComponentsGroupArrayIndex)">
            <div class="my-4">
              <template v-for="assetComponent in assetComponentsGroupArray">
                <AssetComponentTableRow :assetComponent="assetComponent"
                                        :componentsLength="assetComponentsGroupArray.length"
                                        @editAssetComponent="showEditAssetComponentModal"
                                        @onLessorComponentShareUpdated="onLessorComponentShareUpdated"/>
              </template>
            </div>
          </transition>
        </div>
      </template>
    </div>
    <div class="b-contract_row_flex-end mt-5 mr-10">
      <button
        v-if="isContractEventState"
        type="button"
        class="b-btn b-btn_type_empty ml-6"
        @click="cancelAddCounterpartyEvent"
      >{{ $t('CONTENT.cancel') }}
      </button>
      <button
        v-if="isContractEventState"
        type="button"
        class="b-btn b-btn_type_primary ml-6"
        :disabled="checkLessorsForEvent()"
        @click="saveAddCounterpartyEvent"
      >{{ $t('CONTENT.save') }}
      </button>
    </div>
    <popup :name="`asset-component`" height="auto" :scrollable="true">
      <AssetComponentPopup
        v-if="currentAssetComponent"
        :assetComponent="currentAssetComponent"
        :contract="contractObj"
        :is-simplified-asset-component="isSimplifiedAssetComponent"
        @close="closeEditAssetComponentPopup"
      ></AssetComponentPopup>
    </popup>
    <popup :name="`asset`" height="auto" :scrollable="true">
      <AssetPopup
        v-if="newAssetComponent"
        :contract="contractObj"
        :assetComponent="newAssetComponent"
        @addAssetComponent="saveNewAssetComponent"
        @close="closeEditAssetPopup"
      ></AssetPopup>
    </popup>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import AssetComponentTableRow from '../../contracts/edit/AssetComponentTableRow';
import PaymentTableRow from '../../contracts/edit/PaymentTableRow';
import AssetComponentPopup from './rawEditing/AssetComponentPopup';
import dateConverter from '../../../../utils/converters/date.converter';
import AssetPopup from './rawEditing/AssetPopup';
import AssetComponent from '../../../../plugins/assetComponent/models/AssetComponent';
import LessorComponentShares from '../../../../plugins/assetComponent/models/lessorComponentShares';
import AddLessor from "../../../../plugins/eventRegistry/models/commands/AddLessor";
import RemoveLessor from "../../../../plugins/eventRegistry/models/commands/RemoveLessor";
import contract from "@/store/modules/contract";

export default {
  props: ['baseContractObj'],
  components: {
    AssetComponentTableRow,
    PaymentTableRow,
    AssetComponentPopup,
    AssetPopup
  },
  data() {
    return {
      openedGroupedAssetComponents: [],
      groupFilteredAssetComponentsByAssetId: [],
      contractObj: null,
      contractObjInitialForEvent: null,
      currentAssetComponent: null,
      isSimplifiedAssetComponent: null,
      newAssetComponent: null,
      addedLessorIds: [],
      removedLessorIds: []
    }
  },
  mounted() {
    this.$eventBus.$on('saveNewPayment', this.saveNewPayment);
    this.$eventBus.$on('savePaymentEditing', this.savePaymentEditing);
    this.$eventBus.$on('removePayment', this.removePayment);

    this.$eventBus.$on('addAssetComponent', this.saveNewAssetComponent);
    this.$eventBus.$on('saveAssetComponentEditing', this.saveAssetComponentEditing);
    this.$eventBus.$on('cancelEditContract', this.cancelEditContract);
    this.$eventBus.$on('removeAssetLessor', this.removeAssetLessor);
  },
  created() {
    this.contractObj = _.cloneDeep(this.baseContractObj);
    this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
  },
  computed: {
    contract() {
      return contract
    },
    ...mapGetters(["getCurrentContract"]),
    ...mapGetters(['getUnderlyingAssetTypes']),
    ...mapGetters(['getAssets']),
    ...mapGetters(['getUnitOfMeasure']),
    ...mapGetters(['getContractState']),
    ...mapGetters(['getContractStateSimplified']),
    ...mapGetters(["contractIsInClosedPeriod"]),
    ...mapGetters(['getContractConditionsState']),
    ...mapGetters(["getContractSettings"]),
    ...mapGetters(["getRenameLessorList"]),
    ...mapGetters(["getContractEventState"]),
    ...mapGetters(["getLeaseRateChangeEventPaymentIds"]),
    ...mapGetters(["notChangesEventPaymentIds"]),
    currencyCode() {
      return this.getContractSettings.currencyCode;
    },
    isContractChangeConditions() {
      return this.getContractConditionsState;
    },
    isContractRawEditing() {
      return this.getContractState;
    },
    isContractRawSimplifiedEditing() {
      return this.getContractStateSimplified;
    },
    isInClosedPeriod() {
      return this.contractIsInClosedPeriod;
    },
    isContractEventState() {
      return this.getContractEventState;
    },
    assetNames() {
      const assetNames = {}

      this.getAssets.forEach(a => {
        const assetType = this.getAssetType(a.underlyingAssetTypeId)
        assetNames[a.idInDB] = `${assetType}. ${a.name}`
      })

      return assetNames
    },
    assetTypes() {
      const assetTypes = {}

      this.getUnderlyingAssetTypes.forEach(a => {
        assetTypes[a.idInDb] = a.name
      })

      return assetTypes
    }
  },
  watch: {
    isContractEventState(val) {
      if (val === true) {
        this.contractObj = _.cloneDeep(this.baseContractObj);
        this.contractObjInitialForEvent = _.cloneDeep(this.contractObj);
        this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
        this.groupFilteredAssetComponentsByAssetId.forEach((elem, i) => {
          this.setChosenGroupedAssetComponents(i);
        })
      }
    },
    baseContractObj: {
      handler() {
        let assetComponentsChanged = false

        this.getRenameLessorList.forEach((elem) => {
          this.contractObj.contract.lessorCounterpartyIds.forEach((id, i) => {
            if (elem.oldIndex === i) {
              this.contractObj.contract.lessorCounterpartyIds[i] = elem.value;
            }
          })
        });

        const newLessorCounterpartyIds = this.baseContractObj.contract.lessorCounterpartyIds
          .filter(x => !this.contractObj.contract.lessorCounterpartyIds.includes(x));

        const deletedLessorCounterpartyIds = this.contractObj.contract.lessorCounterpartyIds
          .filter(x => !this.baseContractObj.contract.lessorCounterpartyIds.includes(x));
        this.contractObj = _.cloneDeep(this.baseContractObj);

        if (this.getRenameLessorList.length > 0) {
          this.getRenameLessorList.forEach((elem) => {
            this.contractObj.assetComponents.forEach((assetComponent) => {
              assetComponent.lessorComponentShares.forEach((lessor) => {
                if (lessor.lessorId === elem.oldValue) {
                  lessor.lessorId = elem.value;
                }
              })
            })
          })
          this.$store.commit('CLEAR_RENAME_LESSOR_LIST');
          assetComponentsChanged = true
        }

        if (newLessorCounterpartyIds.length > 0) {
          newLessorCounterpartyIds.forEach((newLessorId) => {
            this.contractObj.assetComponents.forEach((assetComponent) => {
              const newLessor = LessorComponentShares.createNew(newLessorId, assetComponent.startDate, assetComponent.endDate);
              assetComponent.lessorComponentShares.push(newLessor);
            });
          });
          assetComponentsChanged = true
        }

        if (deletedLessorCounterpartyIds.length > 0) {
          deletedLessorCounterpartyIds.forEach((deletedLessorId) => {
            this.contractObj.assetComponents.forEach((assetComponent) => {
              assetComponent.lessorComponentShares = assetComponent.lessorComponentShares
                .filter(x => x.lessorId !== deletedLessorId);
            });
          });
          assetComponentsChanged = true
        }

        if (assetComponentsChanged) this.$emit('updateAssetComponents', this.contractObj.assetComponents);
        this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
      },
      deep: true
    },
  },
  methods: {
    getAssetName(assetId) {
      return this.assetNames[assetId]
    },
    getAssetType(assetTypeId) {
      return this.assetTypes[assetTypeId]
    },
    getIsTermination(component) {
      if (component.termination !== null) {
        return true;
      }
    },
    getIsExtension(component) {
      if (component.extension !== null) {
        return true;
      }
    },
    getIsBuyout(component) {
      if (component.buyout !== null) {
        return true;
      }
    },
    getUnitOfMeasureName(unit) {
      for (let i = 0; i < this.getUnitOfMeasure.length; i++) {
        if (this.getUnitOfMeasure[i].code === unit) {
          return this.$t(`CONTENT.${this.getUnitOfMeasure[i].code}`);
        }
      }
    },
    getExtensionDate(component) {
      if (component.extension !== null && component.extension.date !== null) {
        let dateObj = dateConverter.convertToDate(component.extension.date);
        return this.$d(dateObj);
      }
    },
    getBuyoutDate(component) {
      if (component.buyout !== null && component.buyout.date !== null) {
        let dateObj = dateConverter.convertToDate(component.buyout.date);
        return this.$d(dateObj);
      }
    },
    getTerminationDate(component) {
      if (component.termination !== null && component.termination.date !== null) {
        let dateObj = dateConverter.convertToDate(component.termination.date);
        return this.$d(dateObj);
      }
    },
    showItem(assetComponent, assetComponentsLength) {
      this.$eventRegistryManager.editAssetComponentCommands({
        assetComponent: assetComponent,
        assetComponentLength: assetComponentsLength
      });
    },
    setChosenGroupedAssetComponents(groupIndex) {
      if (this.openedGroupedAssetComponents.indexOf(groupIndex) === -1) {
        this.openedGroupedAssetComponents.push(groupIndex);
      } else {
        this.openedGroupedAssetComponents.splice(this.openedGroupedAssetComponents.indexOf(groupIndex), 1);
      }
    },
    isGroupedAssetComponentsShow(groupIndex) {
      return this.openedGroupedAssetComponents.indexOf(groupIndex) !== -1;
    },
    updateComponents() {
      const contract = this.contractObj
      contract.assetComponents.sort(this.compareAssets)

      const result = [];
      let tempGroup = [];
      let grouppedId = contract.assetComponents[0].assetId;

      for (let i = 0; i < contract.assetComponents.length; i++) {
        if (grouppedId != contract.assetComponents[i].assetId) {
          grouppedId = contract.assetComponents[i].assetId;
          result.push(tempGroup);
          tempGroup = [];
        }

        tempGroup.push(contract.assetComponents[i]);
      }

      result.push(tempGroup);
      return result;
    },
    compareAssets(a, b) {
      if (a.assetId === b.assetId) return 0
      return a.assetId < b.assetId ? -1 : 1
    },
    setAssetItem(asset) {
      this.$eventRegistryManager.editAssetCommands(asset[0].assetId);
    },
    async getNewDiscountRateForPayments() {
      this.error = null;
      try {
        let contractPreview = await this.$store.dispatch(
          "getContractPreview",
          this.contractObj.assetComponents
        );
        return contractPreview;
      } catch (e) {
        this.error = e;
      }
    },
    onLessorComponentShareUpdated() {
      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
    },
    async saveNewPayment(data) {

      const assetComponent = this.contractObj.assetComponents
        .find(assetComponent => assetComponent.idInDB === data.assetComponentId)

      const lessorComponentShare = assetComponent.lessorComponentShares
        .find(lessorComponentShare => lessorComponentShare.idInDB === data.lessorComponentShareId)

      lessorComponentShare.paymentsParameters.push(data.newPayment)

      if (!data.isEvent && !this.isContractEventState) {
        await this.setPreviewData();
      }

      // if (!this.isContractEventState) {
      //   this.$emit('updateAssetComponents', this.contractObj.assetComponents);
      // }

        this.$emit('updateAssetComponents', this.contractObj.assetComponents);
      },
      async savePaymentEditing(payment) {
        for (let i = 0; i < this.contractObj.assetComponents.length; i++) {
          for (let q = 0; q < this.contractObj.assetComponents[i].lessorComponentShares.length; q++) {
            for (let j = 0; j < this.contractObj.assetComponents[i].lessorComponentShares[q].paymentsParameters.length; j++) {
              let assetComponent = this.contractObj.assetComponents[i];

            if (assetComponent.lessorComponentShares[q].paymentsParameters[j].idInDB === payment.idInDB) {
              this.$set(assetComponent.lessorComponentShares[q].paymentsParameters, j, payment);
            }
          }
        }
      }

      await this.setPreviewData();
      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
    },
    async removePayment(payment) {
      for (let i = 0; i < this.contractObj.assetComponents.length; i++) {
        for (let q = 0; q < this.contractObj.assetComponents[i].lessorComponentShares.length; q++) {
          for (let j = 0; j < this.contractObj.assetComponents[i].lessorComponentShares[q].paymentsParameters.length; j++) {
            let assetComponent = this.contractObj.assetComponents[i];

            if (assetComponent.lessorComponentShares[q].paymentsParameters[j].idInDB === payment.idInDB) {
              assetComponent.lessorComponentShares[q].paymentsParameters.splice(j, 1);
            }
          }
        }
      }

      await this.setPreviewData();
      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
    },
    saveNewAssetComponent(assetComponent) {
      this.groupFilteredAssetComponentsByAssetId = [];

      assetComponent.lessorComponentShares.forEach(lessorComponentShare => {
        lessorComponentShare.startDate = assetComponent.startDate
        lessorComponentShare.endDate = assetComponent.endDate
      })

      this.contractObj.assetComponents.push(assetComponent);
      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
      this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
    },
    saveAssetComponentEditing(assetComponent) {
      this.groupFilteredAssetComponentsByAssetId = [];

      for (let i = 0; i < this.contractObj.assetComponents.length; i++) {
        if (this.contractObj.assetComponents[i].idInDB === assetComponent.idInDB) {
          this.$set(this.contractObj.assetComponents, i, assetComponent);
        }
      }

      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
      this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
    },
    removeAssetLessor(assetComponent, lessorId) {
      assetComponent.lessorComponentShares = assetComponent.lessorComponentShares
        .filter(lessorComponentShare => lessorComponentShare.lessorId !== lessorId)
      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
      this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
    },
    cancelEditContract() {
      this.groupFilteredAssetComponentsByAssetId = [];

      this.contractObj = _.cloneDeep(this.getCurrentContract);
      this.$emit('updateAssetComponents', this.contractObj.assetComponents);
      this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
    },
    showEditAssetComponentModal(assetComponent, isSimplifiedAssetComponent) {
      this.currentAssetComponent = assetComponent;
      this.isSimplifiedAssetComponent = isSimplifiedAssetComponent;
      this.$modal.show('asset-component', {
        assetComponent,
        isSimplifiedAssetComponent,
      });
    },
    closeEditAssetComponentPopup() {
      this.currentAssetComponent = null;
      this.$modal.hide(`asset-component`);
    },
    showEditAssetModal() {
      this.newAssetComponent = AssetComponent.createNew();
      this.$modal.show(`asset`);
    },
    closeEditAssetPopup() {
      this.newAssetComponent = null;
      this.$modal.hide(`asset`);
    },
    saveAddCounterpartyEvent() {
      const changedIds = this.getLeaseRateChangeEventPaymentIds;
      const deletedIds = this.notChangesEventPaymentIds;
      deletedIds.forEach((id) => {
        changedIds.forEach((elem, i) => {
          if (id === elem.paymentParametersId) {
            changedIds.splice(i, 1);
          }
        })
      })

      const newLessorComponentShares = [];
      const removeLessorComponentShares = [];
      const newPaymentParameters = [];

      this.contractObj.assetComponents.forEach((assetComponent) => {
        assetComponent.lessorComponentShares.forEach((lessor) => {
          if (lessor.isNew && !this.addedLessorIds.some(x => x == lessor.idInDB)) {
            const startDateLessor = dateConverter.convertToDate(lessor.startDate);
            const endDateLessor = dateConverter.convertToDate(lessor.endDate);

            const newLessor = {
              id: lessor.idInDB,
              assetComponentId: assetComponent.idInDB,
              lessorId: lessor.lessorId,
              externalId: lessor.externalId,
              startDateDay: startDateLessor.getDate(),
              startDateMonth: startDateLessor.getMonth() + 1,
              startDateYear: startDateLessor.getFullYear(),
              endDateDay: endDateLessor.getDate(),
              endDateMonth: endDateLessor.getMonth() + 1,
              endDateYear: endDateLessor.getFullYear(),
            };
            newLessorComponentShares.push(newLessor);

            lessor.paymentsParameters.forEach((payment) => {
              const newPayment = {
                lessorComponentShareId: lessor.idInDB,
                ...payment,
              };
              const startDate = dateConverter.convertToDate(newPayment.startDate);
              const endDate = dateConverter.convertToDate(newPayment.endDate);
              const firstPaymentDate = dateConverter.convertToDate(newPayment.firstPaymentDate);

              newPayment.startDateDay = startDate.getDate();
              newPayment.startDateMonth = startDate.getMonth() + 1;
              newPayment.startDateYear = startDate.getFullYear();
              newPayment.endDateDay = endDate.getDate();
              newPayment.endDateMonth = endDate.getMonth() + 1;
              newPayment.endDateYear = endDate.getFullYear();
              newPayment.firstPaymentDateDay = firstPaymentDate.getDate();
              newPayment.firstPaymentDateMonth = firstPaymentDate.getMonth() + 1;
              newPayment.firstPaymentDateYear = firstPaymentDate.getFullYear();

              delete newPayment.startDate;
              delete newPayment.endDate;
              delete newPayment.firstPaymentDate;

              newPaymentParameters.push(newPayment);
            })
            this.addedLessorIds.push(lessor.idInDB);
          }
          if (lessor.isRemove && !this.removedLessorIds.some(x => x == lessor.idInDB)) {
            removeLessorComponentShares.push(lessor.lessorId);
            this.removedLessorIds.push(lessor.idInDB);
          }
        })
      })

      if (newLessorComponentShares.length) {
        const eventDate = new Date(
          newLessorComponentShares[0].startDateYear,
          newLessorComponentShares[0].startDateMonth - 1,
          newLessorComponentShares[0].startDateDay,
        );

        const addLessorCommand = new AddLessor(
          newLessorComponentShares,
          newPaymentParameters,
          changedIds,
          eventDate,
        );
        this.$store.commit("eventRegistryPlugin/addOrUpdateCommand", addLessorCommand);
      }
      if (removeLessorComponentShares.length) {
        const removeLessorCommand = new RemoveLessor(
          this.contractObj.contract.idInDB,
          removeLessorComponentShares[0],
          changedIds,
        );
        this.$store.commit("eventRegistryPlugin/addOrUpdateCommand", removeLessorCommand);
      }
      this.$store.commit('SET_CONTRACT_EVENT_EDITING_STATE', false);
    },
    cancelAddCounterpartyEvent() {
      this.$store.commit('DELETE_ALL_CHANGED_PAYMENT_LEASE_RATE');
      this.$store.commit('DELETE_ALL_CHANGE_PAYMENT');

      this.contractObj = this.contractObjInitialForEvent;
      this.openedGroupedAssetComponents = this.updateComponents();
      this.groupFilteredAssetComponentsByAssetId = this.updateComponents();
      this.groupFilteredAssetComponentsByAssetId.forEach((elem, i) => {
        this.setChosenGroupedAssetComponents(i);
      });
      this.$store.commit('SET_CONTRACT_EVENT_EDITING_STATE', false);
    },
    checkLessorsForEvent() {
      const emptyNewLessor = [];
      this.contractObj.assetComponents.forEach((assetComponent) => {
        assetComponent.lessorComponentShares.forEach((lessor) => {
          if ((lessor.isNew && !lessor.paymentsParameters.length) || lessor.isEditForEvent) {
            emptyNewLessor.push(lessor);
          }
        });
      });

      if (emptyNewLessor.length) {
        return true;
      }
      return false;
    },
    async setPreviewData() {
      let previewData = await this.getNewDiscountRateForPayments();
      if (previewData === undefined) {
        console.log('previewData undefined')
        return
      }

      for (let i = 0; i < this.contractObj.assetComponents.length; i++) {
        for (let j = 0; j < previewData.assetComponents.length; j++) {
          let contractAssetComponent = this.contractObj.assetComponents[i];
          let previewAssetComponent = previewData.assetComponents[j];
          if (contractAssetComponent.idInDB === previewAssetComponent.idInDB) {
            for (let k = 0; k < contractAssetComponent.lessorComponentShares.length; k++) {
              for (let n = 0; n < contractAssetComponent.lessorComponentShares[k].paymentsParameters.length; n++) {
                for (let q = 0; q < previewAssetComponent.lessorComponentShares.length; q++) {
                  for (let l = 0; l < previewAssetComponent.lessorComponentShares[q].paymentsParameters.length; l++) {
                    if (contractAssetComponent.lessorComponentShares[k].paymentsParameters[n].idInDB
                      === previewAssetComponent.lessorComponentShares[q].paymentsParameters[l].idInDB) {
                      this.$set(contractAssetComponent.lessorComponentShares[k].paymentsParameters, n, previewAssetComponent.lessorComponentShares[q].paymentsParameters[l]);
                    }
                  }
                }
              }
            }
          }
        }
      }
    },
  },
  beforeDestroy() {
    this.contractObj = null;
    this.$eventBus.$off('saveNewPayment', this.saveNewPayment);
    this.$eventBus.$off('savePaymentEditing', this.savePaymentEditing);
    this.$eventBus.$off('removePayment', this.removePayment);
    this.$eventBus.$off('saveAssetComponentEditing', this.saveAssetComponentEditing);
    this.$eventBus.$off('cancelEditContract', this.cancelEditContract);
    this.$eventBus.$off('addAssetComponent', this.saveNewAssetComponent);
    this.$eventBus.$off('removeAssetLessor', this.removeAssetLessor);
  }
}
</script>
